import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import { ActiveEnums, ClientPostModel, StateCodePostModel, UpdateClientActivationPostModel } from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

interface Props {
    stateCode?: number;
    activeState?: ActiveEnums;
    stateCodeId?: number;
    clientId?: number;
    search?: string;
}

export default function useClientData({ stateCode, activeState, stateCodeId, clientId, search }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const getClient = useQuery({
        queryKey: [QueryKeys.GetClient, { clientId }],
        queryFn: () => apis.engine.client.clientGetClientList({ clientId }),
        enabled: clientId != undefined,
    });

    const getClientGridQuery = useQuery({
        queryKey: [QueryKeys.ClientGrid, { stateCode, activeState, search }],
        queryFn: () =>
            apis.engine.client.clientGetClientGridList({
                stateCode: stateCode?.toString(),
                activeState,
                searchText: search,
            }),
    });

    const getClientListQuery = useQuery({
        queryKey: [QueryKeys.ClientList, { activeState }],
        queryFn: () => apis.engine.client.clientGetClientListList({ activeState }),
    });

    const getStateCodeQuery = useQuery({
        queryKey: [QueryKeys.GetStateCode, { stateCodeId }],
        queryFn: () => apis.engine.client.clientGetStateCodeList({ id: stateCodeId }),
        enabled: stateCodeId != undefined,
    });

    const saveStateCodeMutation = useMutation({
        mutationFn: (post: StateCodePostModel) => apis.engine.client.clientSaveStateCodeCreate(post),
        onSuccess: (_, { stateCodeId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetSortedStateCode] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetStateCode, { stateCodeId }],
            });
            toast.success("State code saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save state code"),
    });

    const saveClientMutation = useMutation({
        mutationFn: (post: ClientPostModel) => apis.engine.client.clientSaveCreate(post),
        onSuccess: (_, { clientId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.ClientList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.ClientGrid] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetClientCategory, { id: clientId }],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetClient, { clientId }],
            });
            toast.success("Client saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save client"),
    });

    const updateClientActivationMutation = useMutation({
        mutationFn: (post: UpdateClientActivationPostModel) =>
            apis.engine.client.clientUpdateClientActivationCreate(post),
        onSuccess: (_, { clientId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.ClientList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.ClientGrid] });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetClientCategory, { clientId }],
            });
            void queryClient.invalidateQueries({
                queryKey: [QueryKeys.GetClient, { clientId }],
            });
            toast.success("Client status saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save client status"),
    });

    return {
        client: getClient,
        clientGrid: getClientGridQuery,
        clientList: getClientListQuery,
        stateCode: getStateCodeQuery,

        saveStateCode: saveStateCodeMutation,
        saveClient: saveClientMutation,
        updateClientActivation: updateClientActivationMutation,
    };
}
